import React, { useEffect } from 'react';
import { createRoot } from 'react-dom/client';
import axios from 'axios';
import { wixAxiosConfig } from '@wix/wix-axios-config';
import App from './components/App';
import i18n from './i18n';
import { AppProps } from './components/App/App';
import rootReducers from './reducers';
import { ConfigActions as ConfigSharedActionCreators } from '@wix/da-redux-config/pkg/';
import {
  initialize as initPublicSession,
  toggleThemeDone,
} from '@wix/da-shared-react/pkg/publicSession/actions';
import { getIsMobile } from '@wix/da-shared-react/pkg/publicSession/selectors';
import { all, fork, takeEvery } from 'redux-saga/effects';
import { sagas } from '@wix/da-shared-react/pkg/redux/shared';
import DaAppWrapper from '@wix/da-shared-react/pkg/DaApp/DaAppWrapper';
import { setFlags } from '@wix/da-react-context/pkg/flags/redux/actions';
import { flagsFromSession } from '@wix/da-react-context/pkg/flags/helpers';
import { rootModuleStoreFactory } from '@wix/da-shared-react/pkg/redux/rootModuleStore/factory';
import ddt from '@wix/da-ddt';

function* handleThemeSwitchDone() {
  if (window.__HEADER__INIT__.onThemeSwitch) {
    window.__HEADER__INIT__.onThemeSwitch();
  }
}

function* themeSaga() {
  yield takeEvery(toggleThemeDone, handleThemeSwitchDone);
}

function* rootSaga() {
  yield all([...sagas.map(fork), fork(themeSaga)]);
}

export interface HeaderProps extends AppProps {
  locale: string;
  baseURL: string;
  staticsBaseUrl: string;
  publicSession: any;
  features: string[];
  onThemeSwitch: () => void;
  onLoad: () => void;
}

if (window.__HEADER__INIT__) {
  window.__legacy_header__ = true;
  const {
    locale,
    baseURL,
    staticsBaseUrl,
    onLoad,
    activeMainMenuItem,
    features,
    publicSession,
    disableThemeSwitcher,
  } = window.__HEADER__INIT__ as HeaderProps;

  const store: any = rootModuleStoreFactory<any>(
    [],
    ddt.createLogger('legacy-header'),
    {},
    rootReducers,
    rootSaga,
    false
  );

  store.dispatch(
    ConfigSharedActionCreators.initialize({
      csrfToken: publicSession.csrfToken!,
      sharedApiBasePath: 'https://www.deviantart.com/_napi/shared_api/',
      withCredentials: true,
    })
  );
  store.dispatch(initPublicSession({ sessionData: publicSession }));
  store.dispatch(setFlags(flagsFromSession(features))); // where features comes from __HEADER__INIT__

  // Legacy header needs to do cross origin requests for sub domains.
  wixAxiosConfig(axios, { baseURL });
  axios.defaults.withCredentials = true;
  i18n({ locale, baseUrl: staticsBaseUrl });
  const root = createRoot(document.getElementById('da-legacy-header'));
  root.render(
    <React.Suspense fallback={null}>
      <DaAppWrapper store={store}>
        <AppWithCallbackAfterRender
          activeMainMenuItem={activeMainMenuItem}
          publicSession={publicSession}
          features={features}
          disableThemeSwitcher={disableThemeSwitcher}
          store={store}
          onLoad={onLoad}
        />
      </DaAppWrapper>
    </React.Suspense>
  );
}

function AppWithCallbackAfterRender({
  onLoad,
  activeMainMenuItem,
  store,
  publicSession,
  features,
  disableThemeSwitcher,
}) {
  useEffect(() => {
    onLoad();
  });

  return (
    <App
      activeMainMenuItem={activeMainMenuItem}
      isMobile={getIsMobile(store.getState())}
      theme={publicSession.theme}
      themeSkin={publicSession.themeSkin}
      features={features}
      disableThemeSwitcher={disableThemeSwitcher}
    />
  );
}
